import React, { useEffect, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import LeftTextRadius from "../LeftText/LeftTextRadius";
import LeftTextAdditionalInfo from "../LeftText/LeftTextAdditionalInfo";
import './ResultChart.css';
import { useDispatch, useSelector } from "react-redux";
import { setFFR } from "../Store/LoadReducer";
import { formatDecimal } from "../../helpers/utils";

function ResultChart() {

    const uploadedVessels = useSelector(item => item.flowviewer.uploadedVessels);
    const activeVessel = useSelector(item => item.flowviewer.activeVessel);
    const vesselJSON = useSelector(item => item.flowviewer.vesselJSON);
    const viewFFR = useSelector(item => item.flowviewer.viewFFR);
    const dispatch = useDispatch();
    const [activeVesselJSON, setActiveVesselJSON] = useState(false);
    const [smoothingLevel, setSmoothingLevel] = useState(0);
    const [showAdditionalText, setShowAdditionalText] = useState(false);

    useEffect(async () => {
        const _activeVessel = vesselJSON.find(v => v.vessel == activeVessel.toLowerCase());
        if (_activeVessel) {
            setActiveVesselJSON(_activeVessel);
        }
    }, [vesselJSON, activeVessel]);

    function CustomTooltipRadiuses({ payload, active }) {
        if (payload) {
            if (active) {
                return (
                    <div className="custom-tooltip">
                        <p className="mb-0 text-primary fw-bold">{`Diameter : ${formatDecimal(payload[0].value)}`}</p>
                        <p className="mb-0 text-primary fw-bold">{`Position : ${formatDecimal(payload[0].payload.x)}`}</p>
                    </div>
                );
            }
        }
        return null;
    };

    function CustomTooltipFFR({ payload, active }) {
        if (payload) {
            if (active) {
                return (
                    <div className="custom-tooltip">
                        <p className="mb-0 text-warning fw-bold">{`FFR : ${formatDecimal(payload[0].value)}`}</p>
                        <p className="mb-0 text-warning fw-bold">{`Position : ${formatDecimal(payload[0].payload.x)}`}</p>
                    </div>
                );
            }
        }
        return null;
    };

    function getMaxDiameter() {
        let maxD = 6;
        try {
            maxD = uploadedVessels[activeVessel].center_line.reduce((acc, cur) => {
                return Math.max(acc, cur.r);
            }, 0);
            maxD *= 2;
        } finally {
            return maxD > 6 ? maxD : 6;
        }
    };

    const setFFRPoint = (data) => {
        if (activeVessel != "" && data && "activePayload" in data) {
            const point = data.activePayload[0];
            if (point) {
                dispatch(setFFR({
                    vessel: activeVessel,
                    ffr: parseFloat(point.payload.ffr).toFixed(2),
                    x: point.payload.x
                }));
            }
        }
    };

    const tickFormatter = (tickItem) => {
        return formatDecimal(parseFloat(tickItem));
    };

    function getFFR() {
        var result = [];
        var max = [];
        for (let i = 0; i < uploadedVessels[activeVessel].center_line.length; i++) {
            result.push({
                x: parseFloat(uploadedVessels[activeVessel].center_line[i].d).toFixed(1),
                ffr: parseFloat(uploadedVessels[activeVessel].ffr[i])
            });
            max.push(uploadedVessels[activeVessel].ffr[i]);
        }
        Array.prototype.max = function () {
            return Math.max.apply(null, this);
        };
        Array.prototype.min = function () {
            return Math.min.apply(null, this);
        };
        return {
            result: result,
            max: Math.round(max.max() * 100) / 100,
            min: Math.round(max.min() * 100) / 100
        };
    };

    function plotLumenDiameter(smoothingLevel) {
        // First create the original result array
        var result = [];
        for (let i = 0; i < uploadedVessels[activeVessel].center_line.length; i++) {
            result.push({
                x: parseFloat(uploadedVessels[activeVessel].center_line[i].d).toFixed(1),
                D: uploadedVessels[activeVessel].center_line[i].r * 2
            });
        }
        if (smoothingLevel == 0) {
            return result;
        }

        // Then smooth the D values using a simple moving average
        const smoothedResult = [];
        for (let i = 0; i < result.length; i++) {
            let sum = 0;
            let count = 0;
            for (let j = Math.max(0, i - Math.floor((2 * smoothingLevel + 1) / 2)); 
                 j <= Math.min(result.length - 1, i + Math.floor((2 * smoothingLevel + 1) / 2)); j++) {
                sum += result[j].D;
                count++;
            }
            smoothedResult.push({
                x: result[i].x,
                D: sum / count
            });
        }
        
        return smoothedResult;
    }

    function getXAxisTicks(data, ticksCount) {
        var result = [];
        var step = Math.round(data.length / (ticksCount));
        for (let i = 0; i < data.length; i += step) {
            result.push(data[i].x);
        }
        return result;
    };

    const lumenDiameter = plotLumenDiameter(smoothingLevel);
    const ffrPlotData = getFFR();

    const handleWindowSizeChange = (e) => {
        setSmoothingLevel(parseInt(e.target.value) || 0);
    };

    return (
        <div className="position-relative">
            <div className="d-flex align-items-center justify-content-end">
                <button 
                    className="me-4 text-white result-text"
                    onClick={() => setShowAdditionalText(!showAdditionalText)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                   {showAdditionalText ? 'ⓘ Hide additional info' : 'ⓘ Show additional info'}
                </button>
                <span className="me-2 text-white result-text">Smoothing</span>
                <input 
                    type="range" 
                    value={smoothingLevel}
                    onChange={handleWindowSizeChange}
                    min="0"
                    max="5"
                    step="1"
                    className="form-range"
                    style={{ width: "150px" }}
                />
            </div>
            <div className="d-flex align-items-start">
                <div className="d-flex align-items-start">
                    <div className="wrap_chart_scene">
                        {activeVesselJSON && showAdditionalText ? <LeftTextRadius data={activeVesselJSON} /> : ""}
                        <LineChart
                            width={799}
                            height={190}
                            data={lumenDiameter}
                            margin={{ top: 10, right: 0, bottom: 5, left: -15 }}
                        >
                            <CartesianGrid stroke="#2b2b2b" />
                            <Line type="monotone" dataKey="D" stroke="#0d6efd" dot={{ r: 0 }} />
                            <XAxis dataKey="x"
                                ticks={getXAxisTicks(lumenDiameter, 14)}
                                tick={{ fontSize: 13 }}
                                tickFormatter={tickFormatter}
                            />
                            <YAxis domain={[0, getMaxDiameter()]}
                                tickCount={7}
                                interval={0}
                                minTickGap={1}
                                tick={{ fontSize: 13 }}
                                tickFormatter={tickFormatter}
                            />
                            {viewFFR[activeVessel].x ? <ReferenceLine x={viewFFR[activeVessel].x} stroke="red" /> : ""}
                            <Tooltip content={<CustomTooltipRadiuses />} />
                        </LineChart>
                    </div>
                </div>
            </div>
            <div className="d-flex align-items-start">
                <div className="wrap_chart_scene">
                    {'additional_info' in uploadedVessels[activeVessel] && showAdditionalText ? <LeftTextAdditionalInfo data={uploadedVessels[activeVessel].additional_info} /> : ""}
                    <LineChart onClick={(e) => setFFRPoint(e)}
                        width={799}
                        height={190}
                        data={ffrPlotData.result}
                        margin={{ top: 10, right: 0, bottom: 5, left: -15 }}
                    >
                        <CartesianGrid stroke="#2b2b2b" />
                        <Line type="monotone" dataKey="ffr" stroke="#0d6efd" dot={{ r: 0 }} />
                        <XAxis dataKey="x"
                            ticks={getXAxisTicks(ffrPlotData.result, 14)}
                            tick={{ fontSize: 13 }}
                            tickFormatter={tickFormatter}
                        />
                        <YAxis domain={[Math.max(ffrPlotData.min - 0.01, 0), ffrPlotData.max]}
                            tick={{ fontSize: 13 }}
                            tickFormatter={tickFormatter}
                        />
                        {viewFFR[activeVessel].x ? <ReferenceLine x={viewFFR[activeVessel].x} stroke="red" /> : ""}
                        <Tooltip content={<CustomTooltipFFR />} />
                    </LineChart>
                </div>
            </div>
        </div>
    );
}

export default ResultChart;